import { Grid, Stack } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { Coupon } from 'common/contracts';
import { DATE_TIME_FORMAT } from 'constants/constants';
import dayjs from 'dayjs';
import { FormikTouched } from 'formik';
import { FormikErrors } from 'formik';

import AcCard from '../../../../components/AcCard/AcCard';
import AcInput from '../../../../components/AcInput/AcInput';
import Typography from '../../../../components/Typography/Typography';

import '../../../../style/forms.scss';
import { CouponFormValues } from '../CouponsForm';

interface GeneralSettingsProps {
  couponFormValues: CouponFormValues;
  edit: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  errors: FormikErrors<Coupon>;
  touched: FormikTouched<Coupon>;
  setFieldTouched: (field: string, isTouched?: boolean | undefined) => void;
  setFieldValue: (field: string, value: any) => void;
  neverExpires: boolean;
  dup: boolean;
  updatedAt?: string;
}

export default function CouponsGeneralSettings({
  couponFormValues,
  edit,
  handleChange,
  handleBlur,
  errors,
  touched,
  setFieldTouched,
  setFieldValue,
  neverExpires,
  dup,
  updatedAt
}: GeneralSettingsProps) {
  return (
    <AcCard
      stackContainer={false}
      title="General Settings"
      sideHeader={
        updatedAt && (
          <Typography
            tag="span"
            style={{ fontSize: '12px', color: 'green', fontWeight: 'normal' }}
          >
            last updated {dayjs(updatedAt).format(DATE_TIME_FORMAT)}
          </Typography>
        )
      }
      customStyles={{
        border: '1px solid #cacbd4',
        borderRadius: '4px',
        padding: '1em'
      }}
    >
      <Grid container rowSpacing={2} columnSpacing={1.5}>
        <Grid item xs={4}>
          <AcInput
            header="Name"
            name="name"
            value={couponFormValues.name}
            disabled={edit}
            onChange={handleChange}
            onBlur={handleBlur}
            required={true}
            error={Boolean(errors.name) && (touched.name || dup)}
            helperText={touched.name || dup ? errors.name?.toString() : ''}
          />
        </Grid>
        <Grid item xs={4}>
          <AcInput
            header="Discount %"
            name="discountPercentage"
            value={couponFormValues.discountPercentage}
            disabled={edit}
            onChange={handleChange}
            onBlur={handleBlur}
            required={true}
            type="number"
            error={
              Boolean(errors.discountPercentage) && touched.discountPercentage
            }
            helperText={
              touched.discountPercentage
                ? errors.discountPercentage?.toString()
                : ''
            }
          />
        </Grid>
        <Grid item xs={4}>
          <Stack direction="column">
            <h3
              style={{
                fontSize: '12px',
                fontWeight: 'normal',
                marginBottom: '4px'
              }}
            >
              Expires on (UTC)
            </h3>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                timezone="UTC"
                disablePast
                format="DD/MM/YYYY HH:mm"
                value={
                  couponFormValues.expiredBy
                    ? dayjs(couponFormValues.expiredBy)
                    : null
                }
                onChange={(newValue) => {
                  setFieldTouched('expiredBy', true);
                  setFieldValue(
                    'expiredBy',
                    newValue ? newValue.toISOString() : null
                  );
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    height: '48.3px'
                  },
                  '.MuiFormHelperText-root': {
                    marginLeft: 0
                  }
                }}
                disabled={neverExpires}
                slotProps={{
                  textField: {
                    error: Boolean(errors.expiredBy) && touched.expiredBy,
                    helperText:
                      edit || touched.expiredBy
                        ? errors.expiredBy?.toString()
                        : ''
                  }
                }}
              />
            </LocalizationProvider>
          </Stack>
        </Grid>
      </Grid>
    </AcCard>
  );
}
